import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { DateSelection } from "./booking/DateSelection";
import { TimeSelection } from "./booking/TimeSelection";
import { PatientForm } from "./booking/PatientForm";
import { BookingConfirmation } from "./booking/BookingConfirmation";
import { supabase } from "@/integrations/supabase/client";

interface Service {
  title: string;
  description: string;
  tarif: string;
  duration: number | string; // Can be number (minutes) or string (e.g., "3h")
  image: string;
}

interface BookingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  service: Service | null;
  isOnline: boolean;
  isHomeVisit?: boolean;
  eventId?: string;
}

export type BookingStep = "date" | "time" | "form" | "confirmation";

export interface BookingData {
  date: Date | null;
  time: string | null;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  birthDate: Date | null;
  consultationReasons: string[];
  additionalNotes: string;
  smsNumber: string;
  isOnline?: boolean;
  isHomeVisit?: boolean;
  // Home visit address
  homeAddress?: string;
  homePostalCode?: string;
  homeCity?: string;
  homeAccessInfo?: string;
  // Location info for cabinet appointments
  locationId?: string | null;
  locationName?: string | null;
  locationCity?: string | null;
}

export function BookingModal({ open, onOpenChange, service, isOnline, isHomeVisit = false, eventId }: BookingModalProps) {
  // For events, skip date/time selection and go directly to patient form
  const [step, setStep] = useState<BookingStep>(eventId ? "form" : "date");
  const [bookingData, setBookingData] = useState<BookingData>({
    date: null,
    time: null,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    birthDate: null,
    consultationReasons: [],
    additionalNotes: "",
    smsNumber: "",
    isOnline: isOnline,
    isHomeVisit,
    homeAddress: "",
    homePostalCode: "",
    homeCity: "",
    homeAccessInfo: "",
  });
  const [appointmentId, setAppointmentId] = useState<string | null>(null);
  const [childFirstName, setChildFirstName] = useState<string | undefined>(undefined);
  const [practitionerPhone, setPractitionerPhone] = useState<string | undefined>(undefined);
  const [eventDetails, setEventDetails] = useState<{
    date: string;
    time: string;
    format: string;
    duration: string;
  } | null>(null);

  // Fetch event details if eventId is provided
  useEffect(() => {
    if (eventId && open) {
      const fetchEventDetails = async () => {
        const { data } = await supabase
          .from('events')
          .select('event_date, format, duration')
          .eq('id', eventId)
          .single();
        
        if (data && data.event_date) {
          const eventDate = new Date(data.event_date);
          const timeStr = eventDate.toLocaleTimeString("fr-FR", {
            hour: "2-digit",
            minute: "2-digit",
          });
          setEventDetails({
            date: data.event_date,
            time: timeStr,
            format: data.format,
            duration: data.duration,
          });
        }
      };
      fetchEventDetails();
    }
  }, [eventId, open]);

  // Keep booking mode in sync when opening the modal from different CTAs
  useEffect(() => {
    if (!open) return;
    setBookingData((prev) => ({ ...prev, isOnline, isHomeVisit }));
  }, [isOnline, isHomeVisit, open]);

  const handleBack = () => {
    if (step === "time") setStep("date");
    if (step === "form") setStep("time");
  };

  const handleClose = () => {
    setStep(eventId ? "form" : "date");
    setBookingData({
      date: null,
      time: null,
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      birthDate: null,
      consultationReasons: [],
      additionalNotes: "",
      smsNumber: "",
      isOnline,
      isHomeVisit,
      homeAddress: "",
      homePostalCode: "",
      homeCity: "",
      homeAccessInfo: "",
      locationId: null,
      locationName: null,
      locationCity: null,
    });
    setAppointmentId(null);
    setChildFirstName(undefined);
    setPractitionerPhone(undefined);
    onOpenChange(false);
  };

  if (!service) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto p-0" aria-describedby="booking-description">
        <DialogTitle className="sr-only">Réserver un rendez-vous - {service.title}</DialogTitle>
        <div className="grid md:grid-cols-[350px,1fr] min-h-[600px]">
          {/* Left sidebar with service info */}
          <div className="bg-gradient-card border-r p-6 space-y-4">
            <div className="relative h-32 rounded-lg overflow-hidden">
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-full object-cover" loading="lazy" decoding="async" />
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-semibold text-primary" id="booking-description">{service.title}</h3>
              <p className="text-sm text-muted-foreground">{service.description}</p>
              <div className="pt-4 space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Durée:</span>
                  <span className="font-medium">{typeof service.duration === 'number' ? `${service.duration} min` : service.duration}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Tarif:</span>
                  <span className="font-semibold text-secondary">{service.tarif}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Type :</span>
                  <span className="font-medium">{isOnline ? "En visio" : isHomeVisit ? "A domicile" : "Au cabinet"}</span>
                </div>
              </div>

              {step !== "date" && step !== "confirmation" && bookingData.date && bookingData.time && (
                <div className="pt-4 border-t space-y-2">
                  <h4 className="font-semibold text-sm">Récapitulatif</h4>
                  <div className="text-sm space-y-1">
                    <p>
                      {bookingData.date.toLocaleDateString("fr-FR", {
                        weekday: "long",
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </p>
                    <p className="font-medium text-primary">{bookingData.time}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right content area */}
          <div className="p-6">
            {!eventId && step !== "date" && step !== "confirmation" && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="mb-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
            )}

            {!eventId && step === "date" && (
              <DateSelection
                bookingData={bookingData}
                setBookingData={setBookingData}
                onNext={() => setStep("time")}
              />
            )}

            {!eventId && step === "time" && (
              <TimeSelection
                bookingData={bookingData}
                setBookingData={setBookingData}
                onNext={() => setStep("form")}
                onBack={() => setStep("date")}
              />
            )}

            {step === "form" && (
              <PatientForm
                bookingData={bookingData}
                setBookingData={setBookingData}
                service={service}
                isOnline={isOnline}
                isHomeVisit={isHomeVisit}
                eventId={eventId}
                onSuccess={(id, childName, practPhone) => {
                  setAppointmentId(id);
                  setChildFirstName(childName);
                  setPractitionerPhone(practPhone);
                  setStep("confirmation");
                }}
              />
            )}

            {step === "confirmation" && (
              <BookingConfirmation
                bookingData={bookingData}
                service={service}
                isOnline={isOnline}
                isHomeVisit={isHomeVisit}
                appointmentId={appointmentId}
                onClose={handleClose}
                eventDetails={eventDetails || undefined}
                childFirstName={childFirstName}
                practitionerPhone={practitionerPhone}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
